// What a spy brings back, and the face it wears — split out of MissionsTab.

import type { SpyIntel } from '../../espionage/espionage.js';
import { useT } from '../../i18n/index.js';
import { SCENES } from '../scenes.js';

/** What a spy has heard, in one line — or that it has not heard it yet. */
export function IntelReport({
  unitId,
  intel,
}: {
  readonly unitId: string;
  readonly intel: SpyIntel | null;
}) {
  const t = useT();
  if (!intel) {
    return (
      <span className="text-[10px] text-muted-foreground" data-testid={`spy-intel-${unitId}`}>
        {t('espionage.gathering')}
      </span>
    );
  }
  const parts: string[] = [
    intel.attackPrepared ? t('espionage.attackPrepared') : t('espionage.attackNotPrepared'),
  ];
  if (intel.unitCount !== undefined) parts.push(t('espionage.units', { n: intel.unitCount }));
  if (intel.cityGold !== undefined) parts.push(t('espionage.gold', { n: intel.cityGold }));
  if (intel.population !== undefined) parts.push(t('espionage.people', { n: intel.population }));
  if (intel.loyalty !== undefined) parts.push(t('espionage.loyalty', { n: intel.loyalty }));
  if (intel.mood !== undefined) parts.push(t('espionage.mood', { n: intel.mood }));
  if (intel.faith !== undefined) parts.push(t('espionage.faith', { n: intel.faith }));
  if (intel.buildings?.length) {
    parts.push(t('espionage.buildings', { list: intel.buildings.join(', ') }));
  }
  return (
    <span className="text-[10px] text-muted-foreground" data-testid={`spy-intel-${unitId}`}>
      {parts.join(' · ')}
    </span>
  );
}

/** A spy keeps one face for life: the persona is a stable pick from its unit id. */
export function personaOf(unitId: string): (typeof SCENES.spyPersona)[number] {
  let h = 0;
  for (const c of unitId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return SCENES.spyPersona[h % SCENES.spyPersona.length] as (typeof SCENES.spyPersona)[number];
}
